import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/common/Layout';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { ToastProvider } from '@/components/ui/Toast';
import { useAuthStore } from '@/store/auth.store';
import Login from '@/pages/login/Login';
import EntrarGlobal from '@/pages/entrar-global/EntrarGlobal';
import AcceptInvite from '@/pages/auth/AcceptInvite';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import TecnicoJobs from '@/pages/tecnico/TecnicoJobs';
import Dashboard from '@/pages/dashboard/Dashboard';
import Agents from '@/pages/agents/Agents';
import Channels from '@/pages/channels/Channels';
import Conversations from '@/pages/conversations/Conversations';
import ConversationDetail from '@/pages/conversations/ConversationDetail';
import Crm from '@/pages/crm/Crm';
import Contacts from '@/pages/contacts/Contacts';
import Campaigns from '@/pages/campaigns/Campaigns';
import Settings from '@/pages/settings/Settings';
import { InstallationConfigPage } from '@/pages/installation/InstallationConfigPage';
import Users from '@/pages/users/Users';
import Employees from '@/pages/employees/Employees';
import Jobs from '@/pages/jobs/Jobs';
import Despachos from '@/pages/despachos/Despachos';
import Tracking from '@/pages/tracking/Tracking';
import Clientes from '@/pages/clientes/Clientes';
import ClienteDetail from '@/pages/clientes/ClienteDetail';
import Sistema from '@/pages/sistema/Sistema';
import Entrenamiento from '@/pages/entrenamiento/Entrenamiento';
import Consumos from '@/pages/consumos/Consumos';
import Limites from '@/pages/limites/Limites';
import Inspector from '@/pages/inspector/Inspector';
import InspectorAjustes from '@/pages/inspector/InspectorAjustes';
import NuevoNegocio from '@/pages/negocios/NuevoNegocio';
import Agenda from '@/pages/agenda/Agenda';
import Hoy from '@/pages/hoy/Hoy';
import MeToca from '@/pages/metoca/MeToca';
import Personas from '@/pages/personas/Personas';
import FichaDePersona from '@/pages/personas/FichaDePersona';
import AthenaPagina from '@/pages/athena/Athena';
import ChatDeAthena from '@/pages/chat/ChatDeAthena';
import Chats from '@/pages/chats/Chats';
import FrentesPagina from '@/pages/frentes/Frentes';
import Importar from '@/pages/importar/Importar';
import Salud from '@/pages/salud/Salud';
import ClaveDeIA from '@/pages/ajustes/ClaveDeIA';
import Cola from '@/pages/cola/Cola';
import Metricas from '@/pages/cola/Metricas';

/**
 * Shell admin con guardia de rol: un TECNICO no ve el CRM. Se le rebota a su
 * vista acotada (/mis-trabajos). El resto entra al <Layout> completo.
 */
function AdminShell() {
  const role = useAuthStore((s) => s.user?.role);
  if (role === 'TECNICO') return <Navigate to="/mis-trabajos" replace />;
  return <Layout />;
}

/** Router raíz de Conversia. Rutas privadas bajo <Layout> protegido. */
export default function App() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Entrar desde el Conversia global con un pase de un minuto (pages/entrar-global). */}
        <Route path="/entrar-global" element={<EntrarGlobal />} />
        {/* Ciclo de cuentas (páginas públicas): canje de invitación y reset. */}
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Vista del técnico: standalone (sin sidebar/CRM), solo requiere sesión. */}
        <Route
          path="/mis-trabajos"
          element={
            <ProtectedRoute>
              <TecnicoJobs />
            </ProtectedRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <AdminShell />
            </ProtectedRoute>
          }
        >
          {/* La raiz es HOY, no el Dashboard de ventas: el mandato Athena lo
              saca de este producto. El Dashboard sigue existiendo para los
              negocios que si lo usan, en /dashboard. */}
          <Route path="/" element={<Hoy />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/me-toca" element={<MeToca />} />
          <Route path="/personas" element={<Personas />} />
          <Route path="/personas/:id" element={<FichaDePersona />} />
          <Route path="/athena" element={<AthenaPagina />} />
          <Route path="/chats" element={<Chats />} />
          <Route path="/chats/:id" element={<ChatDeAthena />} />
          <Route path="/frentes" element={<FrentesPagina />} />
          <Route path="/importar" element={<Importar />} />
          <Route path="/salud" element={<Salud />} />
          {/* OWNER y no ADMIN: la API de claves solo acepta al dueño. Un admin
              que entra acá veria el formulario y recibiria un 403 al guardar. */}
          <Route
            path="/ajustes/clave-ia"
            element={
              <ProtectedRoute requireRole="OWNER">
                <ClaveDeIA />
              </ProtectedRoute>
            }
          />
          <Route path="/conversations" element={<Conversations />} />
          <Route path="/conversations/:id" element={<ConversationDetail />} />
          <Route path="/crm" element={<Crm />} />
          <Route path="/agents" element={<Agents />} />
          <Route path="/channels" element={<Channels />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/negocios/nuevo"
            element={
              <ProtectedRoute requireRole="OWNER">
                <NuevoNegocio />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agenda"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Agenda />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inspector"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Inspector />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inspector/ajustes"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <InspectorAjustes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/installation"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <InstallationConfigPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Employees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Jobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/despachos"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Despachos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clientes"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Clientes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clientes/:id"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <ClienteDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Tracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              // OWNER, igual que su API: las rutas de usuarios no distinguen
              // negocio, y en manos de un ADMIN permitían resetear la clave del
              // dueño. Ver users.routes.ts.
              <ProtectedRoute requireRole="OWNER">
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sistema"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Sistema />
              </ProtectedRoute>
            }
          />
          <Route
            path="/metricas"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Metricas />
              </ProtectedRoute>
            }
          />
          {/* Relevo de cola (F5.6, E-012). La API responde 404 si el negocio
              no tiene el módulo; el menú tampoco lo ofrece. */}
          <Route
            path="/cola"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Cola />
              </ProtectedRoute>
            }
          />
          <Route
            path="/entrenamiento"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Entrenamiento />
              </ProtectedRoute>
            }
          />
          {/* Reservado del OWNER (F2): consumo USD y límites de gasto. La API
              ya responde 403 a un ADMIN; esto solo evita mostrarle una página
              rota. */}
          <Route
            path="/consumos"
            element={
              <ProtectedRoute requireRole="OWNER">
                <Consumos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/limites"
            element={
              <ProtectedRoute requireRole="OWNER">
                <Limites />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastProvider />
    </>
  );
}
